import React, { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AppBar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  AccessTimeRounded as AccessTimeIcon,
  ArrowBackRounded as ArrowBackIcon,
  CalendarMonthRounded as CalendarIcon,
  LocationOnRounded as LocationIcon,
} from "@mui/icons-material";
import { TerminalCall } from "../../../types/TerminalCall";
import { createSelectedTerminalCall } from "../../../data/selectedterminalcalls";
import { selectedFilteredVoyage } from "../../../selectors/filterSelector";
import { useRefreshVoyageData } from "../../hooks/useRefreshVoyageData";

interface MobileSimpleTerminalCallFormProps {
  open: boolean;
  onClose: () => void;
  terminals?: any[];
  vessel?: any;
}

const getApiId = (value: any, resource: "vessels" | "terminals") => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (value["@id"]) return value["@id"];
  if (value.id) return `/api/${resource}/${value.id}`;
  return null;
};

const getTerminalLabel = (terminal: any) =>
  terminal?.name || terminal?.abbreviation || terminal?.companyName || "";

const getTerminalLocation = (terminal: any) =>
  [terminal?.city, terminal?.country?.name || terminal?.country]
    .filter(Boolean)
    .join(", ");

const roundToNextHalfHour = () => {
  const now = dayjs().second(0).millisecond(0);
  const remainder = now.minute() % 30;
  return remainder === 0 ? now : now.add(30 - remainder, "minute");
};

const MobileSimpleTerminalCallForm: React.FC<
  MobileSimpleTerminalCallFormProps
> = ({ open, onClose, terminals = [], vessel }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { voyage } = useSelector(selectedFilteredVoyage);
  const refreshVoyageData = useRefreshVoyageData();
  const [terminal, setTerminal] = useState<any>(null);
  const [planned, setPlanned] = useState<Dayjs>(roundToNextHalfHour());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setTerminal(null);
    setPlanned(roundToNextHalfHour());
    setSaving(false);
    setError("");
  }, [open]);

  const terminalOptions = useMemo(
    () =>
      [...terminals].sort((left, right) =>
        getTerminalLabel(left).localeCompare(getTerminalLabel(right))
      ),
    [terminals]
  );

  const handleDateChange = (value: string) => {
    const nextDate = dayjs(value);
    if (!nextDate.isValid()) return;

    setPlanned((current) =>
      current
        .year(nextDate.year())
        .month(nextDate.month())
        .date(nextDate.date())
    );
  };

  const handleTimeChange = (value: string) => {
    const [hour, minute] = value.split(":").map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return;

    setPlanned((current) =>
      current.hour(hour).minute(minute).second(0).millisecond(0)
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const vesselId = getApiId(vessel, "vessels");
    const terminalId = getApiId(terminal, "terminals");

    if (!voyage?.["@id"] || !vesselId || !terminalId || !planned.isValid()) {
      setError(
        t("missingRequiredFields", "Please fill in all required fields.")
      );
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        ...new TerminalCall(),
        voyage: voyage["@id"],
        vessel: vesselId,
        terminal: terminalId,
        planned: planned.toISOString(),
        dockingSide: vessel?.preferredDockingSide || "port",
      };

      await dispatch(createSelectedTerminalCall(payload) as any);
      await refreshVoyageData(voyage);
      onClose();
    } catch (submitError) {
      console.error(t("failedToCreatePlanning"), submitError);
      setError(
        t("somethingWentWrong", "Something went wrong. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={saving ? undefined : onClose}
      className="mobile-add-planning-dialog"
    >
      <AppBar position="static" className="mobile-detail-appbar">
        <Toolbar className="mobile-detail-toolbar">
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            disabled={saving}
            aria-label={t("cancel", "Cancel")}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box className="mobile-detail-title">
            <Typography component="h1">
              {t("addPlanning", "Add planning")}
            </Typography>
            <Typography component="p">
              {voyage?.name ||
                voyage?.voyageNumber ||
                vessel?.name ||
                vessel?.abbreviation ||
                ""}
            </Typography>
          </Box>

          <Button
            color="inherit"
            type="submit"
            form="mobile-add-planning-form"
            disabled={saving}
            className="mobile-detail-save"
          >
            {saving ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              t("save", "Save")
            )}
          </Button>
        </Toolbar>
      </AppBar>

      <Box className="mobile-add-planning-content">
        <Box
          id="mobile-add-planning-form"
          component="form"
          className="mobile-add-planning-form"
          onSubmit={handleSubmit}
        >
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box className="mobile-add-planning-card">
            <Typography className="mobile-add-planning-section-title">
              {t("requiredField", { field: t("terminal", "Terminal") })}
            </Typography>

            <Autocomplete
              options={terminalOptions}
              value={terminal}
              onChange={(_event, value) => {
                setTerminal(value);
                setError("");
              }}
              getOptionLabel={getTerminalLabel}
              isOptionEqualToValue={(option, value) =>
                getApiId(option, "terminals") ===
                getApiId(value, "terminals")
              }
              filterOptions={(options, state) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return options;

                return options.filter((option) =>
                  `${getTerminalLabel(option)} ${getTerminalLocation(option)}`
                    .toLowerCase()
                    .includes(query)
                );
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={
                    getApiId(option, "terminals") ||
                    getTerminalLabel(option)
                  }
                >
                  <Box>
                    <Typography>{getTerminalLabel(option)}</Typography>
                    {getTerminalLocation(option) ? (
                      <Typography variant="caption" color="text.secondary">
                        {getTerminalLocation(option)}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t("searchForTerminal", "Search for terminal")}
                  placeholder={t(
                    "startTypingToFindTerminal",
                    "Start typing to find a terminal"
                  )}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationIcon color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box className="mobile-add-planning-card">
            <Typography className="mobile-add-planning-section-title">
              {t("plannedDateTime", "Planned date and time")}
            </Typography>

            <Box className="mobile-add-planning-date-time">
              <TextField
                required
                fullWidth
                type="date"
                label={t("date", "Date")}
                value={planned.format("YYYY-MM-DD")}
                onChange={(event) => handleDateChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                required
                fullWidth
                type="time"
                label={t("time", "Time")}
                value={planned.format("HH:mm")}
                onChange={(event) => handleTimeChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1800 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className="mobile-add-planning-selected">
              <Typography variant="caption">
                {t("selected", "Selected")}
              </Typography>
              <Typography>
                {planned.format("DD/MM/YYYY")} · {planned.format("HH:mm")}{" "}
                {t("localTime", "local time")}
              </Typography>
            </Box>
          </Box>

          <Box className="mobile-add-planning-actions">
            <Button variant="outlined" onClick={onClose} disabled={saving}>
              {t("cancel", "Cancel")}
            </Button>
            <Button variant="contained" type="submit" disabled={saving}>
              {saving ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                t("createPlanning", "Create planning")
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MobileSimpleTerminalCallForm;
